import {
  ARCHIVED_FILTER_NAME,
  DATE_RANGE_FILTER_WITH_OVERDUE_DATE,
  DUE_DATE_FILTER_NAME,
  FILTER_NAME_SUB_MENU,
  FLAT_FILTER_NAME,
  STATUS_FILTER_NAME,
} from '../../../../constants/filter'
import { SUBMENU_DEFAULT_VALUE } from '../../../../constants/route'
import { getDateRangeParam } from '../../../../utils/filter'
import { toText } from '../../../../utils/html'
import { getUserFullName, getUserId } from '../../../../utils/user'
import {
  BIRDVIEW_LIST_SEPARATOR,
  BIRDVIEW_META_SEPARATOR,
  BIRDVIEW_PREVIEW_SEPARATOR,
} from '../../DashboardBirdview.constants'
import {
  BIRDVIEW_OPEN_REQUEST_STATUSES,
  BIRDVIEW_POSTS_SUB_MENU,
} from '../../DashboardBirdview.constants.api'
import { getBirdviewFlatNumberLabel } from '../../DashboardBirdview.utils'

function getBirdviewDwellerAddress(buildings) {
  return buildings
    ?.map(building => building.title)
    .join(BIRDVIEW_META_SEPARATOR)
}

// A chat without a subject is named after the other people in it.
function getBirdviewChatParticipantsNames(users, userId) {
  return users
    ?.reduce((names, participant) => {
      const name = getUserFullName(participant)

      if (name && getUserId(participant) !== userId) {
        names.push(name)
      }

      return names
    }, [])
    .join(BIRDVIEW_LIST_SEPARATOR)
}

function getBirdviewMessagePreview(lastMessage) {
  if (!lastMessage || lastMessage.deleted) {
    return undefined
  }

  return [
    getUserFullName(lastMessage.owner),
    lastMessage.text_preview ? toText(lastMessage.text_preview) : undefined,
  ]
    .filter(Boolean)
    .join(BIRDVIEW_PREVIEW_SEPARATOR)
}

// A dweller sees only their own entities, so the lists carry no owner filter of
// their own: the backend scopes them to the signed-in user.
export function getBirdviewDwellerActiveFilters() {
  return { [ARCHIVED_FILTER_NAME]: false }
}

export function getBirdviewDwellerPostsFilters() {
  return { [FILTER_NAME_SUB_MENU]: BIRDVIEW_POSTS_SUB_MENU }
}

export function getBirdviewDwellerChatsFilters() {
  return { [FILTER_NAME_SUB_MENU]: SUBMENU_DEFAULT_VALUE }
}

// An object row counts every open request of the flat, not only the ones the
// landlord raised.
export function getBirdviewFlatOpenRequestsFilters(flatId) {
  return {
    [ARCHIVED_FILTER_NAME]: false,
    [FLAT_FILTER_NAME]: [flatId],
    [STATUS_FILTER_NAME]: BIRDVIEW_OPEN_REQUEST_STATUSES,
  }
}

export function getBirdviewFlatOverdueRequestsFilters(flatId) {
  return {
    ...getBirdviewFlatOpenRequestsFilters(flatId),
    [getDateRangeParam(DUE_DATE_FILTER_NAME)]: [
      DATE_RANGE_FILTER_WITH_OVERDUE_DATE,
    ],
  }
}

// A scheduled post is dated by its publication, as the newsboard header does.
export function toBirdviewAnnouncementItem(post) {
  return {
    address: getBirdviewDwellerAddress(post.buildings),
    id: post.uuid,
    published: post.date_from ?? post.created,
    title: post.title,
    unread: post.is_unread,
  }
}

export function toBirdviewMessageItem(chat, userId) {
  return {
    id: chat.uuid,
    subtitle:
      getBirdviewMessagePreview(chat.last_message) ||
      getBirdviewDwellerAddress(chat.buildings),
    title: chat.title || getBirdviewChatParticipantsNames(chat.users, userId),
    unread: chat.unread_messages_count > 0,
    updated: chat.last_message?.created ?? chat.updated,
  }
}

// The dweller's own requests have no assignee to show; the flat takes its place.
export function toBirdviewDwellerRequestItem(request) {
  return {
    address: request.building?.title,
    deadline: request.due_date,
    flat: request.flat?.number,
    id: request.id,
    number: request.request_no,
    status: request.status,
    title: request.title,
    unread: request.unread,
  }
}

export function toBirdviewFlatItem(flat, t) {
  return {
    id: flat.id,
    title: [flat.building?.title, getBirdviewFlatNumberLabel(flat.number, t)]
      .filter(Boolean)
      .join(BIRDVIEW_META_SEPARATOR),
  }
}
